// Tegneverktøy (kontrolltiltak/grafikk) via terra-draw + MapLibre-adapter.
// Modus: polygon, linje, sirkel, frihånd, punkt. Rein start/stopp — ett aktivt
// modus om gangen, «velg»-modus for redigering/sletting.
import { type Map as MlMap } from 'maplibre-gl';
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawLineStringMode,
  TerraDrawCircleMode,
  TerraDrawFreehandMode,
  TerraDrawPointMode,
  TerraDrawSelectMode,
  TerraDrawModeUndoRedo,
  TerraDrawSessionUndoRedo,
} from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import type { Kartmodus } from '../map/kartmodus.js';
import { hevVerktoylag } from '../map/verktoylag.js';

export type DrawMode = 'polygon' | 'linestring' | 'circle' | 'freehand' | 'point' | 'select';

export const DRAW_ID = 'draw';

/**
 * Terra-draws egne lag, i den rekkefølgen adapteren lager dem (`prefixId` = 'td').
 * De legges ved `start()`, altså tidlig — og alt som legges senere havner OVER.
 * HV-kommuneflata (`hv-kom-fill`, 42 % rødt fyll) er nettopp et slikt lag, og en
 * påtegning under den kan ikke ses. Rekkefølgen her er nedenfra og opp: fyll,
 * omriss, linje, punkt — punktene (håndtakene man drar i) må ligge øverst.
 */
export const DRAW_LAG = [
  'td-polygon', 'td-polygon-outline', 'td-linestring', 'td-point', 'td-point-marker',
] as const;

export class DrawTool {
  private draw: TerraDraw;
  private started = false;
  private _mode: DrawMode | null = null;
  private selectedId: string | number | null = null;

  constructor(private map: MlMap, private modus?: Kartmodus) {
    this.draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawSelectMode({
          flags: {
            polygon: { feature: { draggable: true, coordinates: { midpoints: true, draggable: true, deletable: true } } },
            linestring: { feature: { draggable: true, coordinates: { midpoints: true, draggable: true, deletable: true } } },
            circle: { feature: { draggable: true } },
            point: { feature: { draggable: true } },
            freehand: { feature: { draggable: true } },
          },
        }),
        new TerraDrawPolygonMode(),
        new TerraDrawLineStringMode(),
        new TerraDrawCircleMode(),
        new TerraDrawFreehandMode(),
        new TerraDrawPointMode(),
      ],
      // ANGRE/GJENTA. Terra-draw har det ferdig; vi sendte bare aldri inn
      // opsjonen (funn 6 i UX-rapporten: «Ingen angre noe sted i COP — mens
      // terra-draw støtter undo()/redo() nativt»). To nivåer, med vilje:
      //   • modeLevel   = angre siste PUNKT mens man tegner
      //   • sessionLevel = angre hele figuren etter at den er ferdig
      // `keyboardShortcuts` sendes IKKE inn: tastene eies av `ui/gester.ts`, og
      // to eiere av ⌘Z ville gitt to angringer på ett trykk.
      undoRedo: {
        modeLevel: new TerraDrawModeUndoRedo(),
        sessionLevel: new TerraDrawSessionUndoRedo(),
      },
    });
    // Spor hvilken figur som er valgt, så «slett valgt» vet hva som skal bort.
    (this.draw as unknown as { on(ev: string, fn: (id: string | number) => void): void })
      .on('select', (id) => { this.selectedId = id; this.onSeleksjon?.(id); });
    (this.draw as unknown as { on(ev: string, fn: () => void): void })
      .on('deselect', () => { this.selectedId = null; this.onSeleksjon?.(null); });
    this.modus?.registrer({ id: DRAW_ID, navn: 'TEGNER', stopp: () => this.avslutt() });
  }

  get mode(): DrawMode | null { return this._mode; }

  setMode(mode: DrawMode) {
    this.modus?.taOver(DRAW_ID);
    if (!this.started) {
      this.draw.start();
      this.started = true;
    }
    this.draw.setMode(mode);
    this._mode = mode;
    // Terra-draws lag må ligge over kartets egne lag. Uten dette tegner
    // operatøren riktig og ser ingenting — og håndtakene han skal dra i ligger
    // under en fyllflate.
    hevVerktoylag(this.map, DRAW_LAG);
  }

  /**
   * Gå ut av tegning helt (Kartmodus stopper oss, eller Escape).
   *
   * Terra-draw fortsetter å eie pekeren i «select», så det holder ikke å bytte
   * modus: vi må til en modus som ikke gjør noe. `static` er terra-draws
   * standard-inaktive modus.
   */
  avslutt() {
    if (this.started) {
      try { this.draw.setMode('static'); } catch { /* eldre versjon uten static */ }
    }
    this._mode = null;
    this.modus?.frigi(DRAW_ID);
  }

  /** Avslutt aktiv tegning, gå til «velg» (redigering) uten å rydde figurer. */
  finish() {
    this.select();
  }

  /** «Velg»-modus: klikk for å merke, dra for å flytte (også hjørner/midtpunkt). */
  select() {
    this.setMode('select');
  }

  /** Slett den valgte figuren. Returnerer true hvis noe ble slettet. */
  deleteSelected(): boolean {
    if (!this.started || this.selectedId == null) return false;
    try {
      (this.draw as unknown as { removeFeatures(ids: (string | number)[]): void })
        .removeFeatures([this.selectedId]);
    } catch {
      return false;
    }
    this.selectedId = null;
    return true;
  }

  /** Er en figur valgt akkurat nå? */
  hasSelection(): boolean { return this.selectedId != null; }

  /** Id-en til figuren som er valgt nå (til seleksjonseieren). */
  get valgtId(): string | number | null { return this.selectedId; }

  /**
   * Meldes når terra-draw selv endrer seleksjonen (kartklikk på en figur).
   *
   * Uten dette ville et kartklikk og et listeklikk endt på to ulike steder: den
   * ene ville satt terra-draws interne seleksjon, den andre COP-ens — nøyaktig
   * fem-øyer-problemet registeret finnes for å avskaffe.
   */
  onSeleksjon?: (id: string | number | null) => void;

  // ── Angre/gjenta ──────────────────────────────────────────────────────────
  //
  // Bare mens tegneverktøyet faktisk er startet. Er det ikke det, skal ⌘Z gå
  // VIDERE til neste ledd i gest-avtalen i stedet for å bli spist av en
  // no-op — en tast som ser ut som den virker er verre enn ingen tast.
  kanAngre(): boolean {
    if (!this.started) return false;
    try { return this.draw.canUndo(); } catch { return false; }
  }

  kanGjenta(): boolean {
    if (!this.started) return false;
    try { return this.draw.canRedo(); } catch { return false; }
  }

  /** Angre. Returnerer om noe faktisk ble angret. */
  angre(): boolean {
    if (!this.kanAngre()) return false;
    try { return this.draw.undo(); } catch { return false; }
  }

  /** Gjenta det sist angrede. */
  gjenta(): boolean {
    if (!this.kanGjenta()) return false;
    try { return this.draw.redo(); } catch { return false; }
  }

  /** Slett alle tegnede figurer. */
  clear() {
    if (this.started) this.draw.clear();
    this.selectedId = null;
  }

  /**
   * Slett BESTEMTE figurer. Nødvendig fordi `clear()` + `onChange`-kringkastingen
   * betyr at «rydd opp etter meg selv» sletter tegninger for ALLE operatører:
   * teig-lagringen konverterte polygonene sine og kalte `clear()`, hvorpå det
   * tomme øyeblikksbildet ble sendt til hele nettet. Returnerer antall slettet.
   */
  remove(ids: (string | number)[]): number {
    if (!this.started || !ids.length) return 0;
    try {
      (this.draw as unknown as { removeFeatures(ids: (string | number)[]): void })
        .removeFeatures(ids);
    } catch {
      return 0;
    }
    if (this.selectedId != null && ids.includes(this.selectedId)) this.selectedId = null;
    return ids.length;
  }

  /** GeoJSON-øyeblikksbilde av alt tegnet (til deling/lagring). */
  snapshot() {
    return this.started ? this.draw.getSnapshot() : [];
  }

  /**
   * Ta MINE EGNE tegninger tilbake etter en reload.
   *
   * ⚠ HVA SOM VAR GALT (eierfunn 2026-08-14): hubben replayer forfatter-settene
   * ved tilkobling, også ditt eget, og klienten la det i den PASSIVE `shared`-
   * kilden. Etter F5 kunne du derfor bare SE dine egne tegninger — ikke merke,
   * flytte, redigere eller slette dem. «Slett alle» gjorde ingenting i det hele
   * tatt, uten feilmelding, fordi terra-draws lager var tomt.
   *
   * Å bare droppe replayen til seg selv ville vært verre: da forsvant tegningene
   * helt. De må inn i MITT lager, som redigerbare figurer.
   *
   * Terra-draw må være startet før features kan legges inn, og vi starter derfor
   * i `static`-modus: gjenopptakelsen skal IKKE stjele kartklikkene fra
   * operatøren (`kartmodus` røres ikke her).
   *
   * Returnerer antall gjenopptatte figurer.
   */
  gjenoppta(features: unknown[]): number {
    if (!features.length) return 0;
    if (!this.started) {
      this.draw.start();
      this.started = true;
      this.draw.setMode('static');
      hevVerktoylag(this.map, DRAW_LAG);
    }
    // Terra-draw avviser hele partiet hvis ÉN feature er ugyldig (f.eks. et av
    // bibliotekets egne håndtak, som havnet i settet vi kringkastet før). Vi
    // legger derfor inn én for én, og hopper over det som ikke godtas — bedre å
    // få tilbake ni av ti tegninger enn null.
    let antall = 0;
    for (const f of features) {
      try {
        (this.draw as unknown as { addFeatures(f: unknown[]): void }).addFeatures([f]);
        antall += 1;
      } catch { /* ugyldig/duplikat figur — hopp over */ }
    }
    return antall;
  }

  onChange(fn: () => void) {
    this.draw.on('change', fn);
  }
}
